import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { formValidator } from "@/services/form/form-validator";
import router from "@/router";
import { i18n } from "@/i18n";
import { findIconDefinition } from "@fortawesome/fontawesome-svg-core";
import { choiceIdentityManager } from "@/services/form/choice-identity-manager";

const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

class FormManager {
  processForm(models, search = false, id = null, deleteDisabledInput = false) {
    let result = {};
    if (models && Object.keys(models).length) {
      Object.values(models).forEach((field) => {
        if (
          field?.vars?.attr &&
          field.vars.attr["hasChildren"] &&
          field.vars.attr["hasChildren"] === true &&
          Object.keys(field?.children).length
        ) {
          result[field.vars.name] = this.processChildren(field, models, search, deleteDisabledInput);
        } else {
          if (deleteDisabledInput) {
            if (!field.vars.disabled) {
              result = this.processField(field, result, models, search);
            }
          } else {
            result = this.processField(field, result, models, search);
          }
        }
      });
    }
    if (id) {
      result = { ...result, id: parseInt(id) };
    }
    return result;
  }

  processChildren(field, models, search, deleteDisabledInput) {
    let childrenResult = {};
    Object.values(field.children).forEach((children) => {
      if (deleteDisabledInput) {
        if (!children.vars.disabled) {
          childrenResult = this.processField(children, childrenResult, models, search);
        }
      } else {
        childrenResult = this.processField(children, childrenResult, models, search);
      }
    });
    return childrenResult;
  }

  // Recherche un champ par vars.name, en descendant dans les enfants des champs "hasChildren"
  // (cf. processChildren) - nécessaire car les erreurs backend et le rechargement de schéma
  // référencent le nom du sous-champ (ex. "answer"), absent du niveau racine de `models`.
  // ATTENTION : suppose vars.name unique au sein d'un même formulaire. En cas de doublon (deux
  // champs "hasChildren" distincts ayant chacun un enfant du même nom), retourne le premier trouvé
  // en parcours DFS sur l'ordre de `models` - pas de garde côté back sur cette unicité à ce jour.
  findFieldByName(models, name) {
    if (!models) {
      return null;
    }
    for (const item of Object.values(models)) {
      if (item?.vars?.name === name) {
        return item;
      }
      if (item?.vars?.attr?.hasChildren === true && item.children && Object.keys(item.children).length) {
        const found = this.findFieldByName(item.children, name);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  getQuery(params) {
    if (typeof params === "string") {
      return params;
    }
    let query = "";
    let index = 0;
    if (params) {
      Object.entries(params).forEach((param) => {
        // Une valeur groupée ("[1,raw]") est déjà encodée valeur par valeur au moment de sa
        // construction : ses crochets/virgules sont la syntaxe du format, pas des valeurs à encoder.
        let isPreEncodedGroup = false;
        if (param[1] !== null && typeof param[1] === "object" && !Array.isArray(param[1])) {
          let searchValue = "[";
          let i = 0;
          Object.values(param[1]).forEach((object) => {
            let value = null;
            if (object && object.id) {
              value = object.id;
            } else if (typeof object !== "object") {
              value = object;
            }
            if (value) {
              if (i) {
                searchValue = searchValue.concat(",");
              }
              searchValue = searchValue.concat(encodeURIComponent(value));
            }
            i++;
          });
          searchValue = searchValue.concat("]");
          param[1] = searchValue;
          isPreEncodedGroup = true;
        }
        if (
          param[0] &&
          typeof param[1] !== "undefined" &&
          param[1] !== null &&
          param[1].toString() &&
          param[0] !== "_token"
        ) {
          if (index) {
            query = query.concat("&");
          }
          query = query.concat(encodeURIComponent(param[0]) + "=");
          if (Array.isArray(param[1])) {
            param[1].forEach((el, arrIndex) => {
              if (arrIndex) {
                query = query.concat(",");
              }
              if (typeof el === "object" && el.id) {
                query = query.concat(encodeURIComponent(el.id));
              } else {
                query = query.concat(encodeURIComponent(el));
              }
            });
          } else {
            query = query.concat(isPreEncodedGroup ? param[1] : encodeURIComponent(param[1]));
          }
          index++;
        }
      });
    }
    return query;
  }

  parseQuery(payload) {
    if (!payload || typeof payload !== "string") {
      return payload || {};
    }
    return Object.fromEntries(new URLSearchParams(payload));
  }

  getAjaxIdentifier(itemModel) {
    const value = itemModel?.vars?.value;
    if (itemModel?.vars?.attr?.["data-ajax-id"] === "value") {
      return value?.value ?? null;
    }
    if (value && value.data?.id) {
      return value.data.id;
    }
    // Choix de chips/cartes (ChoiceChipTypeComponent, ChoiceGridTypeComponent) : la valeur est la donnée du choix
    // ({id, ...} ou {cryptedId, ...}), ou une liste de ces données en mode multiple - les ids sont joints par une virgule pour tenir en un segment d'URL.
    if (Array.isArray(value)) {
      return value.map((item) => item?.cryptedId ?? item?.id ?? item).join(",") || null;
    }
    if (value && !value.data && value.id) {
      return value.id;
    }
    return value ? value.data : null;
  }

  async fieldLoad(itemModel, $this = null, id = null, prefix = "", models, type = null) {
    return new Promise((resolve) => {
      id = id ? id : this.getAjaxIdentifier(itemModel);
      if (id) {
        Object.entries(itemModel.vars.attr["data-ajax-load"]).forEach((ajax) => {
          const target = ajax[0];
          const url = ajax[1] + "/" + prefix + encodeURIComponent(id);
          if (models) {
            this.getAjaxTargetModel(models, target, type).loading = true;
          }
          apiConnection
            .get(url)
            .then((data) => {
              if (target && $this) {
                $this.$emit("ajaxTargetLoad", {
                  choices: { ...data },
                  target: target,
                });
              } else {
                resolve(data);
              }
            })
            .finally(() => {
              if (models) {
                this.getAjaxTargetModel(models, target, type).loading = false;
              }
            });
        });
      }
    });
  }

  fieldSearch(itemModel, search, models, type = null) {
    const [target, url] = Object.entries(itemModel.vars.attr["data-ajax-load"])[0];
    const targetModel = this.getAjaxTargetModel(models, target, type);
    targetModel.loading = true;
    return apiConnection
      .get(url, search ? { search } : {})
      .then((response) => {
        targetModel.vars.total = response?.total;
        return response?.data ?? [];
      })
      .finally(() => {
        targetModel.loading = false;
      });
  }

  getAjaxTargetModel(models, target, type = null) {
    return type === "trainingCourse_duplication" ? models.referenceColumn.children[target] : models[target];
  }

  processResponse(data, route, successMessage, models) {
    if (data && !data.hasError) {
      if (route) {
        router.push(route);
      }
      notificationManager.showNotification("success", successMessage);
    } else if (data.responses && data.responses[0].validationErrors) {
      const validationErrors = data.responses[0].validationErrors;
      notificationManager.showNotification("error", this.resolveValidationErrorMessage(validationErrors));
      formValidator.processResponses(validationErrors, models);
    }
  }

  // Affiche le message de l'unique erreur bloquante (déjà traduit côté back) plutôt que le toast
  // générique, qui masquait la raison réelle du rejet. Dès qu'il y a plusieurs erreurs, ou que
  // l'unique erreur est une alerte (context.alertId, gérée comme telle par le validator), on garde
  // le toast générique pour ne pas masquer les autres champs en faute. Partagé avec
  // contractActivityFormMixin.submitContractActivityForm(), qui applique la même règle sur un
  // formulaire à onglets.
  resolveValidationErrorMessage(validationErrors) {
    const singleBlockingError =
      Array.isArray(validationErrors) && 1 === validationErrors.length
        ? validationErrors.find((error) => !error.context?.alertId)
        : null;
    return singleBlockingError?.message ?? i18n.global.t("signed_error");
  }

  processReporting(data, route, successMessage) {
    let reports = {
      recap: [],
      report: [],
    };

    if (data && (!data.reporting || (data.reporting && !data.reporting.length))) {
      router.push(route);
      notificationManager.showNotification("success", successMessage);
    } else if (data && data.reporting && data.reporting.length) {
      notificationManager.showNotification("error", i18n.global.t("signed_error"));
      Object.values(data.reporting).forEach((report) => {
        reports["report"].push(this.processReport(report));
      });
      const lastElement = data.reporting[data.reporting.length - 1];
      for (const [key, value] of Object.entries(lastElement.logCount)) {
        reports["recap"].push(this.processReportRecap(key, value));
      }
    }
    return reports;
  }

  processReport(report) {
    return (
      i18n.global.tc("global.import.error", {
        line: report.reporting.line,
        column: report.reporting.column,
      }) +
      " " +
      report.reporting.log
    );
  }

  processReportRecap(key, value) {
    return i18n.global.t(key) + " : " + value;
  }

  isInputType(model) {
    return (
      (this.isTextType(model) ||
        this.isNumberType(model) ||
        this.isIntegerType(model) ||
        this.isEmailType(model) ||
        this.isPasswordType(model)) &&
      model.vars.block_prefixes[2] !== "textarea" &&
      model.vars.block_prefixes[2] !== "range" &&
      model.vars.block_prefixes[2] !== "color" &&
      model.vars.block_prefixes[2] !== "icon" &&
      model.vars.block_prefixes[3] !== "slider" &&
      model.vars.block_prefixes[3] !== "zama_tiny_mce_type"
    );
  }

  isTitleType(model) {
    return model.vars.block_prefixes[1] === "title";
  }

  isApprenticeSalariesCollectionType(model) {
    return (
      model.vars.block_prefixes[1] === "collection" &&
      model.vars.block_prefixes[2] === "_registration_salary_block_apprenticeSalaries"
    );
  }

  isSubTitleType(model) {
    return model.vars.block_prefixes[1] === "subtitle";
  }

  isModalInformationType(model) {
    return model.vars.block_prefixes[1] === "modal_information";
  }

  // Valeur confirmée directement dans le code back (SumCaptchaType::getBlockPrefix(),
  // src/Form/Type/SumCaptchaType.php) - pas un défaut Symfony dérivé du nom de classe.
  isSumCaptchaType(model) {
    return model.vars.block_prefixes[1] === "sum_captcha";
  }

  isAlertType(model) {
    return model.vars.block_prefixes[1] === "alert";
  }

  isMailStatusType(model) {
    return model.vars.block_prefixes[1] === "mail_status";
  }

  isNumberType(model) {
    return model.vars.block_prefixes[1] === "number";
  }

  isTextType(model) {
    return model?.vars?.block_prefixes[1] === "text" && model?.vars?.block_prefixes[2] !== "range";
  }

  isSubmitType(model) {
    return model.vars ? model.vars.block_prefixes[1] === "submit" : false;
  }

  isIntegerType(model) {
    return model.vars.block_prefixes[1] === "integer";
  }

  isEmailType(model) {
    return model.vars.block_prefixes.includes("email");
  }

  isPasswordType(model) {
    return (
      model.vars.block_prefixes[1] === "password" ||
      (model.vars.block_prefixes[1] === "text" && model.vars.block_prefixes[2] === "password")
    );
  }

  isAjaxSearch(model) {
    return model.vars && model.vars.attr && model.vars.attr["data-ajax-url"];
  }

  isEmptyValue(value) {
    return value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length);
  }

  isAjaxLoad(model) {
    return model.vars.attr && model.vars.attr["data-ajax-load"] && model.vars.attr["data-ajax-id"];
  }

  isAccordion(model) {
    return model.vars.attr && model.vars.attr["data-checkbox-accordion"];
  }

  isPhoneType(model) {
    return model.vars.block_prefixes[1] === "phone_number";
  }

  isChoiceType(model) {
    return model.vars.block_prefixes[1] === "choice";
  }

  isHiddenType(model) {
    return model?.vars?.block_prefixes?.[1] === "hidden";
  }

  isTextareaType(model) {
    return (
      model.vars.block_prefixes[1] === "text" &&
      model.vars.block_prefixes[2] === "textarea" &&
      model.vars.block_prefixes[3] !== "zama_tiny_mce_type"
    );
  }

  isEditorType(model) {
    return (
      model.vars.block_prefixes[1] === "text" &&
      model.vars.block_prefixes[2] === "textarea" &&
      model.vars.block_prefixes[3] === "zama_tiny_mce_type"
    );
  }

  isDateType(model) {
    return (
      model.vars?.block_prefixes[1] === "date" ||
      model.vars?.block_prefixes[1] === "datetime" ||
      model.vars?.block_prefixes[1] === "time"
    );
  }

  isTimeType(model) {
    return model.vars.block_prefixes[1] === "time";
  }

  isTimeRefType(model) {
    return (
      model.vars.block_prefixes[1] === "time" &&
      model.vars.attr &&
      (model.vars.attr["data-combine-hour"] || model.vars.attr["data-combine-day"])
    );
  }

  isForcedNotSwitch(model) {
    return !!model?.vars?.attr?.["data-force-not-switch"];
  }

  isRadioType(model) {
    return (
      this.isChoiceType(model) &&
      model.vars.expanded &&
      !model.vars.multiple &&
      !model?.vars?.attr?.["data-choice-chip"] &&
      (typeof model.vars.data !== "boolean" || this.isForcedNotSwitch(model)) &&
      !model?.vars?.attr?.["data-force-switch"]
    );
  }

  // Variante "chip" d'un choix, simple (ex. niveau d'études, data-choice-chip) ou multiple (ex.
  // origines de contact, data-choice-chips) - composant autonome unique (ChoiceChipTypeComponent),
  // jamais RadioTypeComponent/CheckboxTypeComponent. Le composant lui-même distingue les deux cas
  // via model.vars.multiple (déjà fourni tel quel par les deux FormType back concernés,
  // PlacementDomainCatalogSearchType/LandingOriginType) plutôt que via un second attr dédié.
  // Exclusion symétrique posée sur isRadioType() (data-choice-chip) et isCheckboxType()
  // (data-choice-chips) ci-dessous.
  isChoiceChipType(model) {
    return (
      this.isChoiceType(model) &&
      model?.vars?.expanded &&
      !!(model?.vars?.attr?.["data-choice-chip"] || model?.vars?.attr?.["data-choice-chips"])
    );
  }

  isSwitchType(model) {
    return (
      !this.isForcedNotSwitch(model) &&
      !model?.vars?.attr?.["data-toggle-card"] &&
      (model.vars.block_prefixes[1] === "switch" ||
        model.vars.block_prefixes[1] === "checkbox" ||
        // ST143-TA-5304 (port develop) : champ 1/0-normalisé forcé en switch.
        model?.vars?.attr?.["data-force-switch"] ||
        (this.isChoiceType(model) &&
          typeof model.vars.data === "boolean" &&
          model.vars.attr &&
          !model.vars.attr["data-form-list"]))
    );
  }

  // Case à cocher rendue en carte cliquable (ex. "unknownTrainingChoice"/"wantsCallback") - composant
  // autonome (ToggleCardTypeComponent), jamais SwitchTypeComponent. Exclusion symétrique posée sur
  // isSwitchType() ci-dessus, même mécanisme que data-force-not-switch.
  isToggleCardType(model) {
    return !!model?.vars?.attr?.["data-toggle-card"];
  }

  isCheckboxType(model) {
    return (
      this.isChoiceType(model) &&
      model.vars.expanded &&
      model.vars.multiple &&
      typeof model.vars.data !== "boolean" &&
      ((model.vars.attr &&
        !model.vars.attr["data-form-list"] &&
        !model.vars.attr["data-choice-grid"] &&
        !model.vars.attr["data-choice-chips"] &&
        !model.vars.attr["data-choice-card-list"]) ||
        !model.vars.attr)
    );
  }

  // Variante "liste de cartes avec cupo" d'un choix multiple (ex. formations retenues, max N) -
  // composant autonome (ChoiceCardListTypeComponent), jamais CheckboxTypeComponent. Exclusion
  // symétrique posée sur isCheckboxType() ci-dessus (même mécanisme que data-form-list/
  // data-choice-grid/data-choice-chips).
  isChoiceCardListType(model) {
    return (
      this.isChoiceType(model) &&
      model.vars.expanded &&
      model.vars.multiple &&
      !!(model.vars.attr && model.vars.attr["data-choice-card-list"])
    );
  }

  // Variante "grille de cartes" d'un choix multiple (couleur/icône/compteur par choice) - composant
  // autonome (ChoiceGridTypeComponent), jamais CheckboxTypeComponent. Exclusion symétrique posée
  // sur isCheckboxType() ci-dessus (même mécanisme que data-form-list).
  isChoiceGridType(model) {
    return (
      this.isChoiceType(model) &&
      model.vars.expanded &&
      model.vars.multiple &&
      !!(model.vars.attr && model.vars.attr["data-choice-grid"])
    );
  }

  isSelectType(model) {
    return (
      !model.vars.expanded &&
      this.isChoiceType(model) &&
      (typeof model.vars.data !== "boolean" || this.isForcedNotSwitch(model)) &&
      (!model.vars.attr ||
        (model.vars.attr && !model.vars.attr["data-form-list"] && !model.vars.attr["data-template-variables"]))
    );
  }

  isArrayChoicesType(model) {
    return model.vars.block_prefixes[1] === "array_choices";
  }

  isTemplateVariableType(model) {
    return model.vars.attr && model.vars.attr["data-template-variables"];
  }

  isEntityType(model) {
    return model.vars.block_prefixes[2] === "entity";
  }

  isMultiple(model) {
    return model.vars.multiple;
  }

  isFileType(model) {
    return (
      model.vars.block_prefixes[1] === "file" ||
      model.vars.block_prefixes[1] === "single_file_upload" ||
      model.vars.block_prefixes[1] === "file_upload"
    );
  }

  isFileDropzoneType(model) {
    return model.vars.dropzone;
  }

  isListType(model) {
    return this.isChoiceType(model) && model.vars.attr && model.vars.attr["data-form-list"];
  }

  isSliderType(model) {
    return (
      model.vars.block_prefixes[1] === "text" &&
      model.vars.block_prefixes[2] === "range" &&
      model.vars.block_prefixes[3] === "slider"
    );
  }

  isColorType(model) {
    return model?.vars?.block_prefixes?.[1] === "color_picker";
  }

  isIconType(model) {
    return model?.vars?.block_prefixes?.[1] === "icon_picker";
  }

  // Vérifie que le nom nu correspond bien à une icône enregistrée dans la bibliothèque light
  // (cf. library.add(fas, fal) dans main.js)
  isValidIconName(name) {
    return Boolean(findIconDefinition({ prefix: "fal", iconName: name }));
  }

  isRoleType(model) {
    return model.vars.block_prefixes[1] === "collection" && model.vars.block_prefixes[2] === "_role_permissions";
  }

  isFormType(model) {
    return model.vars.block_prefixes[1] === "user_address" || model.vars.block_prefixes[1] === "planning_recurrence";
  }

  isCollectionType(model) {
    return model.vars.block_prefixes[1] === "collection" && model.vars.attr["data-collection"] !== false;
  }

  processField(field, result, models, search = false, isCollection = false) {
    if (this.isSelectType(field) && !this.isMultiple(field) && this.isEntityType(field)) {
      this.processSelectEntity(field, result, search, isCollection);
    } else if (this.isSelectType(field) && !this.isEntityType(field) && !this.isMultiple(field)) {
      this.processSelect(field, result, isCollection);
    } else if (this.isRadioType(field) || this.isSwitchType(field)) {
      this.processSwitchAndRadio(field, result, isCollection);
    } else if (this.isCheckboxType(field)) {
      this.processCheckbox(field, result, search, isCollection);
    } else if (this.isChoiceChipType(field)) {
      this.processChoiceChip(field, result, isCollection);
    } else if (this.isChoiceGridType(field) || this.isChoiceCardListType(field)) {
      this.processChoiceMultiple(field, result, isCollection);
    } else if (this.isToggleCardType(field)) {
      // "unknownTrainingChoice" (jamais de field.touched) est donc toujours envoyé.
      // "wantsCallback" ne l'est que si l'utilisateur a cliqué la carte -
      // touched === false (posé par CandidacyLandingFormView au bootstrap) retient la clé pour que
      // normalizePayload() (back) ne touche pas à une préférence déjà enregistrée.
      if (field.touched !== false) {
        result[this.setFieldName(field, isCollection)] = !!field.vars.value;
      }
    } else if (this.isSelectType(field) && this.isMultiple(field)) {
      this.processSelectMultiple(field, result, isCollection);
    } else if (this.isNumberType(field)) {
      this.processNumber(field, result, isCollection);
    } else if (this.isFileType(field)) {
      this.processFile(field, result, isCollection);
    } else if (this.isFormType(field)) {
      result = this.processSubForm(field, result);
    } else if (this.isSumCaptchaType(field)) {
      this.processSumCaptcha(field, result, isCollection);
    } else if (this.isTimeRefType(field)) {
      this.processDate(field, result, models, isCollection);
    } else if (this.isColorType(field)) {
      this.processColor(field, result, isCollection);
    } else if (this.isIconType(field)) {
      this.processIcon(field, result, isCollection);
    } else if (this.isTextType(field) || this.isTextareaType(field)) {
      this.processText(field, result);
    } else if (this.isArrayChoicesType(field)) {
      this.processArrayChoicesType(field, result);
      this.processText(field, result, isCollection);
    } else if (this.isCollectionType(field)) {
      this.processCollectionType(field, result);
    } else if (this.isDateType(field)) {
      this.processDate(field, result, models, isCollection);
    } else {
      result[this.setFieldName(field, isCollection)] = field.vars.value ? field.vars.value : null;
    }
    return result;
  }

  setFieldName(field, isCollection = false) {
    let name = field.vars.name;
    if (isCollection) {
      name = name.split("-")[0];
    }
    return name;
  }

  processCollectionType(field, result) {
    let form = [];
    Object.values(field.children).forEach((child) => {
      let childCollection = {};
      Object.values(child.children).forEach((model) => {
        this.processField(model, childCollection, field.children, false, true);
      });
      form.push(childCollection);
    });
    result[field.vars.name] = form;
  }

  processSelectEntity(field, result, search = false, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    if (field.vars.value && field.vars.value.value) {
      if (search) {
        result[name] = field.vars.value.value;
      } else {
        result[name] = {
          id: field.vars.value.value,
        };
      }
    } else if (field.vars.value && typeof field.vars.value === "string") {
      result[field.vars.name] = { id: field.vars.value };
    } else {
      result[name] = null;
    }
  }

  processSelectMultiple(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = [];
    if (field?.vars?.value) {
      Object.values(field.vars.value).forEach((value) => {
        result[name].push(
          field.vars.block_prefixes[2] === "entity" && typeof value.data === "object"
            ? { id: value.data?.id }
            : value.data,
        );
      });
    }
  }

  processSelect(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    if (typeof field.vars.value === "boolean") {
      result[name] = field.vars.value;
    } else {
      result[name] = field.vars.value ? field.vars.value.data : null;
    }
  }

  processSwitchAndRadio(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    let choice;
    if (field?.vars?.choices && Array.isArray(field.vars.choices)) {
      const rawValue = field?.vars?.value;
      const comparableValue = typeof rawValue !== "boolean" ? (rawValue ? rawValue.toString() : "") : rawValue;
      choice = field.vars.choices.find((item) => item.value === comparableValue);
      if (!choice && typeof rawValue === "boolean") {
        // ST143-TA-5304 (port develop) : champ choice non booléen (data) forcé en switch —
        // SwitchTypeComponent écrit un booléen JS natif au toggle alors que choice.value
        // (ChoiceView Symfony) est toujours une string ("1"/"0"). Fallback limité aux
        // choices "1"/"0" (revue front !1787) pour ne pas masquer un data-force-switch
        // posé par erreur sur des choices "yes"/"no".
        const mappedValue = rawValue ? "1" : "0";
        if (field.vars.choices.some((item) => item.value === "1" || item.value === "0")) {
          choice = field.vars.choices.find((item) => item.value === mappedValue);
        }
      }
    }
    result[name] = typeof field.vars.data === "boolean" ? !!Number(field.vars.value) : choice ? choice.data : null;
  }

  processCheckbox(field, result, search, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = [];
    if (field.vars.value && Object.keys(field.vars.value).length) {
      Object.values(field.vars.value).forEach((value) => {
        if (value.choices) {
          Object.values(value.choices).forEach((choice) => {
            if (choice.checked) {
              result[name].push(choice.data);
            }
          });
        } else {
          if (value.checked) {
            let data;
            if (field.vars.block_prefixes[2] === "entity" && search && value.data.id) {
              data = value.data.id;
            } else {
              data = value.data;
            }
            result[name].push(data);
          }
        }
      });
    }
  }

  // Composant autonome (ChoiceChipTypeComponent, simple data-choice-chip ou multiple
  // data-choice-chips) écrivant directement dans vars.value (déjà la valeur finale - array pour le
  // multiple, scalaire sinon), jamais le format "choix annotés checked" qu'attend processCheckbox()
  // ci-dessus - d'où cette rame dédiée plutôt que de laisser isCheckboxType() le capter à tort.
  processChoiceChip(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = field.vars.value ?? (field.vars.multiple ? [] : null);
  }

  // Revue Loïc (ST304-TA-4918) : ChoiceGridTypeComponent/ChoiceCardListTypeComponent tombaient dans
  // le `else` générique plus bas, qui envoie vars.value tel quel (l'entité complète de chaque choix).
  // Ça fonctionne aujourd'hui seulement parce que le back (App\Form\Util\SubmittedChoiceValue)
  // tolère aussi bien la donnée entière que sa valeur - ce n'est pas une sérialisation voulue ici.
  // Extraction explicite de l'identité de chaque choix (cryptedId, sinon id - choiceIdentityManager,
  // même repli que SubmittedChoiceValue::scalar() côté back), toujours un tableau (jamais null, même
  // vide), cf. convention déjà posée par processSelectMultiple/processChoiceChip (mode multiple).
  processChoiceMultiple(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = (field.vars.value || [])
      .map((value) => choiceIdentityManager.identity(value))
      .filter((identity) => null !== identity);
  }

  processNumber(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = parseFloat(field.vars.value);
  }

  processFile(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    if (!field.vars.dropzone && field.vars.value && field.vars.value.id) {
      result[name] = { id: field.vars.value.id };
    } else if (field.vars.dropzone && field.vars.value) {
      result[name] = [];
      Object.values(field.vars.value).forEach((file) => {
        result[name].push(file.id);
      });
    } else {
      result[name] = null;
    }
  }

  // "captcha" n'a pas attr.hasChildren côté back (contrairement à "contact", cf. commentaire de
  // CandidacyLandingFormView.vue::submit()) - processField() ne peut donc pas passer par le
  // mécanisme générique hasChildren/processChildren pour descendre dans ses enfants
  // (firstNumber/secondNumber/token/answer). Mettre "sum_captcha" dans isFormType() casserait le
  // rendu (ModelComponent.vue réutilise isFormType() pour choisir le template, en plus de
  // SumCaptchaTypeComponent déjà branché sur isSumCaptchaType() - les deux se déclencheraient en
  // double) - d'où cette branche dédiée. Seuls token et answer sont utiles au back
  // (PlacementLandingController::submitProspect(), payload {captcha: {token, answer}}) -
  // firstNumber/secondNumber ne sont que l'affichage de la question.
  processSumCaptcha(field, result, isCollection = false) {
    const token = this.findFieldByName(field.children, "token");
    const answer = this.findFieldByName(field.children, "answer");
    result[this.setFieldName(field, isCollection)] = {
      token: token?.vars?.value ?? null,
      answer: answer?.vars?.value ?? null,
    };
  }

  processSubForm(field, result) {
    if (field.vars.block_prefixes[1] === "user_address" || field.vars.block_prefixes[1] === "planning_recurrence") {
      let subFormResult = this.processForm(field.children);
      if (field.vars.value && field.vars.value.id) {
        subFormResult = {
          ...subFormResult,
          id: parseInt(field.vars.value.id),
        };
      }
      if (field.vars.block_prefixes[1] === "planning_recurrence") {
        result = { ...result, planningRecurrence: subFormResult };
      } else if (field.vars.block_prefixes[1] === "user_address") {
        result = { ...result, personalAddress: subFormResult };
      }
    }
    return result;
  }

  processDate(field, result, models, isCollection = false) {
    let name;
    let ref = null;
    Object.values(models).forEach((model) => {
      if (
        !ref &&
        ((model.children && !Object.keys(model.children).length) ||
          model.vars.block_prefixes[1] === "time" ||
          model.vars.block_prefixes[1] === "date" ||
          model.vars.block_prefixes[1] === "datetime")
      ) {
        ref =
          field.vars?.attr &&
          (model.vars.name === field.vars?.attr["data-combine-day"] ||
            model.vars.name === field.vars?.attr["data-combine-hour"])
            ? model
            : null;
      } else if (!ref) {
        ref = Object.values(model.children).find(
          (item) =>
            field.vars?.attr &&
            (item.vars.name === field.vars?.attr["data-combine-hour"] ||
              item.vars.name === field.vars?.attr["data-combine-day"]),
        );
      }
    });
    name = this.setFieldName(field, isCollection);
    if (ref) {
      if (field.vars.attr["data-combine-day"]) {
        result[name] = ref.vars.value.split(" ")[0] + " " + field.vars.value;
      } else if (field.vars.attr["data-combine-hour"]) {
        name = this.setFieldName(ref, isCollection);
        result[name] = ref.vars.value.concat(" " + field.vars.value);
      }
    } else if (field.vars.value && typeof field.vars.value === "object") {
      let value = null;
      if (field.vars.value.year && field.vars.value.day && field.vars.value.month) {
        value = field.vars.value.year + "-" + field.vars.value.month + "-" + field.vars.value.day;
      }
      result[name] = value;
    } else {
      result[name] = field.vars.value ? field.vars.value : null;
    }
  }

  processText(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    if (field.vars.attr && field.vars.attr["data-type-array"] && typeof field.vars.value === "string") {
      result[name] = field.vars.value ? field.vars.value.replace(/[;_\-\s:]/g, ",").split(",") : [];
    } else {
      result[name] = field.vars.value ? field.vars.value : "";
    }
  }

  processColor(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    result[name] = field.vars.value ? String(field.vars.value) : null;
  }

  processIcon(field, result, isCollection = false) {
    const name = this.setFieldName(field, isCollection);
    const value = field.vars.value;
    if (field.vars.multiple === true) {
      if (Array.isArray(value)) {
        result[name] = value.map((icon) => String(icon));
      } else {
        result[name] = value ? [String(value)] : [];
      }
    } else {
      result[name] = value ? String(value) : null;
    }
  }

  processArrayChoicesType(field, result) {
    result[field.vars.name] = field.vars.value;
  }

  async checkToggleTarget(models, model, keepHideValue = false) {
    if (model && model.vars && model.vars.toggler_target) {
      let value;
      let targetClass = "";
      if (model.vars.value && model.vars.value.data) {
        if (model.vars?.attr?.["isEntity"]) {
          value = model.vars.value?.attr["toggle_value"];
        } else {
          value = model.vars.value.data;
        }
        targetClass = "." + model.vars.toggler_target + "-" + value;
      } else if (typeof model.vars.value === "object") {
        let index = 0;
        if (model.vars.value && Object.keys(model.vars.value).length) {
          Object.values(model.vars.value).forEach((choice) => {
            if (choice?.checked || (this.isSelectType(model) && this.isMultiple(model))) {
              targetClass = targetClass.concat(
                (index ? ", " : "") + "." + model.vars.toggler_target + "-" + choice.value,
              );
              index++;
            }
          });
        }
      } else {
        value = model.vars.value;
        targetClass = "." + model.vars.toggler_target + "-" + value;
        if (typeof value === "boolean") {
          targetClass = targetClass.concat(", ." + model.vars.toggler_target + "-" + (value === true ? 1 : 0));
        }
      }

      let elementsToHide = await this.getElementsToHide(model.vars.toggler_target);

      let elementsToShow;
      if (targetClass) {
        targetClass = targetClass.replaceAll("\\", "");
        if (
          this.type === "questionnaire-add" &&
          models.useCrewDates.vars.value === false &&
          models.useSameDates.vars.value === true
        ) {
          elementsToShow = document.querySelectorAll(".useCrewDates-false:not(.useSameDates-false)");
        } else {
          elementsToShow = document.querySelectorAll(targetClass);
        }

        for (let i = 0; i < elementsToHide.length; i++) {
          const elementToHide = elementsToHide[i];
          elementToHide.classList.add("d-none");
          this.ignoreElementFields(elementToHide, true);
        }

        for (let i = 0; i < elementsToShow.length; i++) {
          const elementToShow = elementsToShow[i];
          elementToShow.classList.remove("d-none");
          this.ignoreElementFields(elementToShow);
        }

        let currentValue =
          model.vars.value && typeof model.vars.value === "object" ? model.vars.value.value : model.vars.value;
        currentValue =
          currentValue && typeof currentValue === "string" ? currentValue.replaceAll("\\", "") : currentValue;
        if (typeof currentValue === "boolean") {
          currentValue = currentValue ? 1 : 0;
        }

        let fieldsToShow = [];
        this.setFieldsToToggle(models, model, currentValue, fieldsToShow, true, false);

        fieldsToShow.forEach((field) => {
          field.vars.ignoreValidation = false;
        });

        let fieldsToHide = [];
        this.setFieldsToToggle(models, model, currentValue, fieldsToHide, false, true);

        fieldsToHide.forEach((field) => {
          if (!keepHideValue) {
            const emptyByType = { boolean: false, string: "", number: null };
            if (Array.isArray(field.vars.value)) {
              field.vars.value = [];
            } else {
              field.vars.value = formManager.isPhoneType(field) ? "" : (emptyByType[typeof field.vars.value] ?? {});
            }
          }
          field.vars.ignoreValidation = true;
        });
      }
    }
  }

  getElementsToHide(searchClass) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(document.querySelectorAll('[class*="' + searchClass + '"]'));
      }, 1);
    });
  }

  setFieldsToToggle(models, model, currentValue, fieldsToHide, targetValue, hide) {
    Object.values(models).forEach((item) => {
      if (item.vars?.attr && item.vars.attr["hasChildren"] && item.children && Object.keys(item.children).length) {
        Object.values(item.children).forEach((children) => {
          this.checkItemToAddToFieldsArray(model, currentValue, children, fieldsToHide, targetValue, hide);
        });
      }
      this.checkItemToAddToFieldsArray(model, currentValue, item, fieldsToHide, targetValue, hide);
    });
  }

  toggleValueVariants(value) {
    if (value === 1 || value === "1" || value === true || value === "true") {
      return ["1", "true"];
    }
    if (value === 0 || value === "0" || value === false || value === "false") {
      return ["0", "false"];
    }
    return [String(value)];
  }

  checkItemToAddToFieldsArray(model, currentValue, item, fieldsArray, targetValue, hide = false) {
    const itemToggleClass = item.vars.toggle_class ? item.vars.toggle_class.replaceAll("\\", "") : null;
    if (!itemToggleClass) return;

    const includeTogglerTarget = hide ? itemToggleClass.includes(model.vars.toggler_target) : true;
    const variants = this.toggleValueVariants(currentValue);
    const escapedTarget = escapeRegExp(model.vars.toggler_target);
    // toggle_class est une chaîne de type sélecteur CSS : les classes y sont
    // séparées par un espace ou un point (sélecteur composé `.a.b`). Les listes
    // construites par concat plus haut utilisent `", "` : l'espace fait alors
    // office de frontière, donc [\s.] suffit à couvrir ce cas. La frontière
    // stricte évite qu'un `target-1` ne matche par erreur `target-10`.
    const matches = variants.some((variant) =>
      new RegExp(`(^|[\\s.])${escapedTarget}-${escapeRegExp(variant)}([\\s.]|$)`).test(itemToggleClass),
    );
    const isTargetValue = targetValue ? matches : !matches;

    if (includeTogglerTarget && isTargetValue) {
      fieldsArray.push(item);
    }
  }

  ignoreElementFields(el, ignore) {
    let fields;
    if (["DIV", "SPAN", "FORM"].includes(el.tagName)) {
      fields = el.querySelectorAll("input, select, textarea");
      for (let j = 0; j < fields.length; j++) {
        if (!ignore) {
          fields[j].classList.remove("ignore-validation");
        } else {
          fields[j].classList.add("ignore-validation");
        }
      }
    }
  }

  fetchConditionalChildren(data, registraitonField) {
    return apiConnection
      .post("/app/registration/field-fetcher/fetch-children/" + registraitonField, data)
      .then((res) => res);
  }

  setAutoSelectedValuesFromChoices(model, value) {
    if (model.vars?.choices && Array.isArray(model.vars.choices)) {
      if (model.vars.multiple) {
        model.vars.value = [];
      } else {
        model.vars.value = null;
      }
      model.vars.choices.forEach((choice) => {
        if (choice.attr?.selected) {
          if (model.vars.multiple) {
            model.vars.value.push(choice);
          } else {
            model.vars.value = choice;
          }
        }
      });
    } else {
      model.vars.value = value;
    }
  }

  getDataFromValue(value) {
    const isStringOrNumber = (v) => typeof v === "string" || typeof v === "number";

    if (value == null || value === "") return null;
    if (isStringOrNumber(value)) return value;
    if (typeof value !== "object") return null;

    const { data } = value;
    if (data == null) return null;
    if (isStringOrNumber(data)) return data;
    if (typeof data === "object") return data.id ?? data.data ?? data.value ?? null;

    return null;
  }

  isModelVisible(model) {
    return !this.isHiddenType(model);
  }
}

export const formManager = new FormManager();
